import { Injectable } from '@angular/core';
import { TargetCandidate, WebRecordedCommand, WebRecordedStep } from '../Models/web-recorded-command-model';
import { CTVModel } from '../Models/ctv-model';
import { webObjectToCandidates } from '../Models/web-object-candidates';
import { buildPlainEnglishSentence, PlainEnglishTargetInfo } from './plain-english';

const STORAGE_KEY = 'webRecorderSteps';

// Shared between the sidebar steps list, the main-area per-step commands
// view, and the simulator toolbar — all three need to read and mutate the
// same steps data, so it lives here rather than being passed through
// @Input()/@Output() chains between components that aren't parent/child of
// each other.
//
// Persisted to localStorage on every mutation and restored on load — a
// recorder session survives a page refresh server-side (pingSession() picks
// the active Selenoid session back up), so the step list the user was
// building needs to survive the same refresh, not vanish from memory.
@Injectable({ providedIn: 'root' })
export class RecordedStepsService {

  steps: WebRecordedStep[] = [];

  // Drives the main-area view swap (Simulator <-> Commands view). Set only
  // by clicking a step in the sidebar list, or closed via the commands
  // view's own close button.
  selectedStep: WebRecordedStep | null = null;

  // Drives which step newly recorded commands append to. Kept separate from
  // selectedStep (rather than collapsed into one field) because going back
  // to the list (selectStep(null)) must not interrupt recording — commands
  // keep appending to the last-opened step even while the list is showing.
  recordingStep: WebRecordedStep | null = null;

  private stepCounter = 0;
  private commandCounter = 0;

  // Last URL seen in a real "open" command — lets appendCommand() re-insert
  // an "open" row if the recorded one gets deleted mid-recording (see
  // appendCommand()). Not persisted; recomputed live, only meaningful while
  // actively recording.
  private lastKnownOpenUrl: string | null = null;

  constructor() {
    this.restore();
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        steps: this.steps,
        selectedStepId: this.selectedStep ? this.selectedStep.id : null,
        recordingStepId: this.recordingStep ? this.recordingStep.id : null,
        stepCounter: this.stepCounter,
        commandCounter: this.commandCounter
      }));
    } catch (e) { /* storage unavailable/full — keep working in-memory only */ }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      this.steps = saved.steps || [];
      this.stepCounter = saved.stepCounter || 0;
      this.commandCounter = saved.commandCounter || 0;
      this.selectedStep = this.steps.find(s => s.id === saved.selectedStepId) || null;
      this.recordingStep = this.steps.find(s => s.id === saved.recordingStepId) || null;
    } catch (e) { /* corrupt/unreadable — start fresh */ }
  }

  reset(): void {
    this.steps = [];
    this.selectedStep = null;
    this.recordingStep = null;
    this.lastKnownOpenUrl = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { }
  }

  newStep(name?: string): WebRecordedStep {
    const step: WebRecordedStep = {
      id: ++this.stepCounter,
      name: (name && name.trim()) || 'New Step',
      commands: []
    };
    this.steps.push(step);
    this.recordingStep = step;
    this.persist();
    return step;
  }

  // Called when clicking a step in the sidebar list — both views it AND
  // makes it the recording target.
  selectStep(step: WebRecordedStep | null): void {
    this.selectedStep = step;
    if (step) this.recordingStep = step;
    this.persist();
  }

  // Pulls one backend-saved step into the local list as an ordinary,
  // editable step (not a separate read-only section). Idempotent — a step
  // already loaded by backend _id is left alone, so it's safe to re-run.
  loadSavedStep(existingStep: { _id: string; name: string }, ctvCommands: CTVModel[]): WebRecordedStep | null {
    const already = this.steps.find(s => s.savedStepId === existingStep._id);
    if (already) return already;

    const commands = ctvCommands.map(c => this.ctvToWebRecordedCommand(c));
    const step: WebRecordedStep = {
      id: ++this.stepCounter,
      name: (existingStep.name || '').trim() || 'New Step',
      commands,
      savedStepId: existingStep._id,
      savedCommandCount: commands.length
    };
    this.steps.push(step);
    this.persist();
    return step;
  }

  private ctvToWebRecordedCommand(c: CTVModel): WebRecordedCommand {
    // "open url" has no target — URL lives in `value` on the backend but goes in `targets[0]` locally.
    const isOpen = c.command && c.command.name === 'open url';
    const candidates = isOpen ? [] : webObjectToCandidates(c.target);
    return {
      id: ++this.commandCounter,
      command: (c.command && c.command.name) || '',
      targets: isOpen ? [String(c.value || '')] : candidates.map(cd => cd.value),
      value: isOpen ? '' : (c.value != null ? String(c.value) : ''),
      isPassword: !!c.value1SecureMode,
      timestamp: '',
      title: isOpen ? undefined : ((c.target && c.target.name) || (candidates[0] && candidates[0].value) || undefined),
      targetCandidates: candidates.length ? candidates : undefined,
      englishDescription: c.english_description || undefined
    };
  }

  // Shape matches CodeTestIDE's own Recorder.record() message exactly:
  // { command, target: [[locatorString, strategyLabel], ...], value,
  // insertBeforeLastCommand, frameLocation, frameArbor }. `target` is
  // singular and each candidate is a [locator, label] pair — flatten to
  // just the locator strings for our targets: string[] model. CodeTestIDE
  // doesn't mask passwords at all (isPassword is never sent), so nothing
  // here gets shown masked — a real, accepted behavior difference from the
  // old hand-written capture script.
  appendCommand(raw: any): void {
    // Defensive: only ever a real recorded command should reach here. If a
    // control/signal message (e.g. the context-menu-opened ping) ever
    // slipped past its own handler upstream, it would otherwise show up as
    // a visible, empty row — silently drop anything with no command name
    // instead.
    if (!raw || !raw.command) return;

    if (!this.recordingStep) {
      this.recordingStep = this.newStep();
    }
    // Mirrors the reference recorder's own rule for auto-inserting "open"
    // (its background script only does so `if (getRecordsArray().length
    // === 0)` — i.e. only for a still-empty test case). ataserver has no
    // visibility into whether the step already has commands, so it emits
    // "open" on every startRecording() call — this is the actual gate,
    // applied where the step data lives, so stopping and restarting
    // recording into the same step doesn't pile up duplicate "open" rows.
    if (raw.command === 'open' && this.recordingStep.commands.length > 0) {
      return;
    }
    if (raw.command === 'open') {
      const openTargets: any[] = raw.target || raw.targets || [];
      const openUrl = openTargets.length ? (Array.isArray(openTargets[0]) ? openTargets[0][0] : openTargets[0]) : null;
      if (openUrl) this.lastKnownOpenUrl = String(openUrl);
    }
    // cdp-bootstrap.js's send() appends an ["englishDescription:<sentence>",
    // "englishDescription"] pair onto every command's target array (mirrors
    // the reference extension's own command_grid.js addCommand()) — pull it
    // out before it's treated as a real locator candidate, same as
    // extractTitle/extractCandidates below must never see it either.
    const allTargets: any[] = raw.target || raw.targets || [];
    const englishEntry = allTargets.find(t => Array.isArray(t) && t[1] === 'englishDescription');
    const englishDescription = englishEntry
      ? String(englishEntry[0]).replace(/^englishDescription:/, '')
      : undefined;
    const rawTargets: any[] = englishEntry ? allTargets.filter(t => t !== englishEntry) : allTargets;
    const targets = rawTargets.map(t => Array.isArray(t) ? t[0] : t);
    const command: WebRecordedCommand = {
      id: ++this.commandCounter,
      command: raw.command,
      targets,
      value: raw.value || '',
      isPassword: !!raw.isPassword,
      timestamp: raw.timestamp,
      title: this.extractTitle(rawTargets),
      targetCandidates: this.extractCandidates(rawTargets),
      englishDescription
    };
    // CodeTestIDE's own recorder registers more than one handler under the
    // same DOM event (e.g. clickAt alongside its canvas-click variants,
    // recorder-handlers.js) — on some elements a single real click fires
    // more than one of them, each independently emitting an identical
    // command. Rather than making the user notice and run "Remove
    // Duplicate Clicks" after the fact, drop it live using the exact same
    // comparison that action already uses.
    const last = this.recordingStep.commands[this.recordingStep.commands.length - 1];
    if (last && last.command === command.command && last.value === command.value
      && JSON.stringify(last.targets) === JSON.stringify(command.targets)) {
      return;
    }
    // Deleting the recorded "open" row (or clearing the step entirely) mid-
    // recording leaves nothing that would ever re-trigger one — a fresh
    // "open" only ever comes from Java at the start of startRecording(), and
    // recording was never stopped here. Same page, so the last URL we saw
    // is still accurate — re-synthesize it in front of the next real command
    // instead, matching the extension's own "an empty test case always
    // starts with open" rule.
    if (command.command !== 'open'
      && !this.recordingStep.commands.some(c => c.command === 'open')
      && this.lastKnownOpenUrl) {
      this.recordingStep.commands.unshift({
        id: ++this.commandCounter,
        command: 'open',
        targets: [this.lastKnownOpenUrl],
        value: '',
        isPassword: false,
        timestamp: raw.timestamp
      });
    }
    this.recordingStep.commands.push(command);
    this.persist();
  }

  // Same idea as the reference recorder's own grid display: show a human
  // label instead of a raw locator when one's available. Prefer the
  // explicit text-based candidate locatorBuilders.js produces (labeled
  // "textContent"); fall back to pulling quoted text out of any locator
  // string (the reference grid's own fallback when parsing its saved
  // target strings for display).
  private extractTitle(rawTargets: any[]): string | undefined {
    const textPair = rawTargets.find(t => Array.isArray(t) && t[1] === 'textContent');
    if (textPair && textPair[0]) return String(textPair[0]).trim();

    for (const t of rawTargets) {
      const locator = Array.isArray(t) ? t[0] : t;
      if (typeof locator !== 'string') continue;
      const match = locator.match(/['"]([^'"]{1,60})['"]/);
      if (match && match[1].trim()) return match[1].trim();
    }
    return undefined;
  }

  // Exact same exclusion list as the reference extension's own Element
  // Details table (showElementTarget(), background/panel/js/UI/command_grid.js
  // ~line 306) — internal/experimental locator strategies (AI self-healing
  // candidates, visual/pixel-based metadata, etc.) that exist in
  // CodeTestIDE's captured data but were never meant to be shown as a
  // human-facing locator option.
  private static readonly ELEMENT_DETAILS_EXCLUDED_TYPES = new Set([
    'cropscreenshot', 'dom-path', 'verticalIndex', 'datasetLocator', 'sizeRatio',
    'element-size', 'xpath:parent', 'xpath:custom-attributes', 'xpath:ancestor-count',
    'xpath:following-sibling', 'xpath:preceding-text', 'xpath:nth-child',
    'xpath:contains-class', 'xpath:style-attribute', 'xpath:title-attribute',
    'xpath:partial-text', 'xpath:aria-attributes', 'xpath:next-sibling-text',
    'xpath:relativeTextSibling', 'xpath:structureAndVisualPosition',
    'xpath:computedStyle', 'xpath:viewportPosition', 'xpath:childStructure',
    'xpath:semanticContext', 'xpath:elementChain', 'xpath:dynamicPath',
    'cssFeatures', 'dynamicPath', 'content-hash', 'uuid-locator', 'css-matrix',
    'timestamp-locator', 'pixel-color', 'bounding-box', 'element-index',
    'audio-waveform', 'random-noise-pattern', 'verticalPosition', 'visual-features',
    'relative-coordinates', 'xpath:input-type'
  ]);

  // CodeTestIDE sends every locator strategy it tried for the clicked
  // element, not just the one we display — [locatorString, strategyLabel]
  // pairs (e.g. ["//span[.='Parquet']", "labelBased Xpath"]). We only ever
  // used the first entry's value for `targets`; this keeps the full set,
  // labels included, for the "Element Details" view (matches the gear-icon
  // popup in the reference extension).
  private extractCandidates(rawTargets: any[]): TargetCandidate[] | undefined {
    if (!rawTargets || rawTargets.length === 0) return undefined;
    return rawTargets
      .filter(t => {
        if (!Array.isArray(t)) return true;
        const label = t[1];
        const value = t[0];
        if (typeof label === 'string' && RecordedStepsService.ELEMENT_DETAILS_EXCLUDED_TYPES.has(label)) return false;
        // Belt-and-suspenders: a few of the excluded labels above carry
        // "ai_parameter"-prefixed values too, but if a future locator
        // builder adds a new ai_parameter-value candidate under a label not
        // yet in that list, this still keeps it out.
        if (typeof value === 'string' && value.startsWith('ai_parameter')) return false;
        return true;
      })
      .map(t => Array.isArray(t)
        ? { label: t[1] != null ? String(t[1]) : '', value: String(t[0]) }
        : { label: '', value: String(t) }
      );
  }

  deleteStep(step: WebRecordedStep): void {
    this.steps = this.steps.filter(s => s.id !== step.id);
    if (this.selectedStep && this.selectedStep.id === step.id) this.selectStep(null);
    if (this.recordingStep && this.recordingStep.id === step.id) this.recordingStep = null;
    this.persist();
  }

  renameStep(step: WebRecordedStep, name: string): void {
    const trimmed = name.trim();
    if (trimmed) step.name = trimmed;
    this.persist();
  }

  copyStep(step: WebRecordedStep): WebRecordedStep {
    const copy: WebRecordedStep = {
      id: ++this.stepCounter,
      name: step.name + ' (copy)',
      commands: step.commands.map(c => ({ ...c, id: ++this.commandCounter }))
    };
    const idx = this.steps.findIndex(s => s.id === step.id);
    this.steps.splice(idx + 1, 0, copy);
    this.persist();
    return copy;
  }

  // Collapses consecutive commands that are identical in command name,
  // targets, and value — the common case of a double-fired click event
  // producing two indistinguishable rows in a row.
  removeDuplicateClicks(step: WebRecordedStep): void {
    step.commands = step.commands.filter((c, i) => {
      if (i === 0) return true;
      const prev = step.commands[i - 1];
      return !(prev.command === c.command
        && prev.value === c.value
        && JSON.stringify(prev.targets) === JSON.stringify(c.targets));
    });
    this.persist();
  }

  removeCommand(step: WebRecordedStep, commandId: number): void {
    step.commands = step.commands.filter(c => c.id !== commandId);
    this.persist();
  }

  removeAllCommands(step: WebRecordedStep): void {
    step.commands = [];
    this.persist();
  }

  // Matches the extension's own grid, where the value column is an
  // editable cell — e.g. correcting a verifyText's expected value after
  // recording rather than only being able to delete and re-record it.
  updateCommandValue(step: WebRecordedStep, commandId: number, value: string): void {
    const command = step.commands.find(c => c.id === commandId);
    if (!command) return;
    command.value = value;
    this.persist();
  }

  // Single-slot clipboard, at the service level (not the component) so a
  // copy made while viewing one step is still there after switching to
  // another — matching the reference recorder's own copy/paste menu.
  private commandClipboard: WebRecordedCommand | null = null;

  copyCommand(command: WebRecordedCommand): void {
    this.commandClipboard = { ...command };
  }

  hasClipboardCommand(): boolean {
    return this.commandClipboard !== null;
  }

  pasteCommand(step: WebRecordedStep): void {
    if (!this.commandClipboard) return;
    step.commands.push({ ...this.commandClipboard, id: ++this.commandCounter });
    this.persist();
  }

  // Fallback for commands recorded before englishDescription existed (or
  // before a given backend fix landed) — that data is frozen as captured
  // and toggling the switch can't retroactively rewrite it, but every
  // command still carries enough raw locator/value data to compose the
  // same sentence on the fly, so this is used wherever command.
  // englishDescription itself would otherwise be shown.
  descriptionFor(command: WebRecordedCommand): string | undefined {
    if (command.englishDescription) return command.englishDescription;
    if (RecordedStepsService.NO_ELEMENT_COMMANDS.has(command.command)) {
      return buildPlainEnglishSentence(command.command, command.targets[0] || '', command.value);
    }
    const candidates = command.targetCandidates || [];
    const elementDesc = candidates.find(c => c.label === 'elementDescription');
    const plainEnglishName = candidates.find(c => c.label === 'plainEnglishName');
    let targetInfo: string | PlainEnglishTargetInfo;
    if (elementDesc) {
      targetInfo = elementDesc.value;
    } else if (plainEnglishName) {
      try {
        targetInfo = JSON.parse(plainEnglishName.value) as PlainEnglishTargetInfo;
      } catch {
        targetInfo = command.title || command.targets[0] || '';
      }
    } else {
      targetInfo = command.title || command.targets[0] || '';
    }
    return buildPlainEnglishSentence(command.command, targetInfo, command.value);
  }

  private static readonly NO_ELEMENT_COMMANDS = new Set(['open', 'selectWindow', 'close']);
}
